import React from 'react';
import { makeAutoObservable } from 'mobx';

import { TimePeriod } from 'controls/react/form/timePeriodSelector';
import { Service } from 'framework/entities/service';
import { ApplicationState } from 'framework/applicationState';
import { Permission } from 'framework/permission';
import { getCurrentTimeStringInTimeZone, isTimeZoneDifferentFromCurrent } from 'tools/dateTimeUtils';
import { apiFetch } from 'framework/api';
import { getService, getServiceModel, ServiceModelResponse } from 'areas/services/api';
import { TimePeriodType } from 'controls/react/form/timePeriodType';
import { MxCell } from 'controls/designer/mxGraphInterfaces';
import { GridStore } from 'controls/grid/gridStore';
import { SummaryDocument, summaryDocuments } from 'areas/services/services-grid/api';
import { GridSelectionType } from 'controls/grid/gridConfiguration';
import { Link } from 'controls/react/link';
import moment from 'moment';
import Renderer from 'tools/renderer';
import Settings from 'settings';
import { State } from 'tools';
import { PlainResponseRemoteDataProvider } from 'controls/grid/plainResponseRemoteDataProvider';
import { parseDateFormat } from 'tools/dateTime/parseDateFormat';
import { translator } from 'core/localization';

export const i18n = translator({
	'Service Time': {
		en: 'Service time',
		no: 'Tjenestetid',
	},
	'Service is Shared': {
		en: 'Share service',
		no: 'Del tjenesten',
	},
});

export const ServiceDetailsContext = React.createContext<ServiceDetailsStore>(null);

export type ServiceDetailsProps = {
	id: string;
	updateMode?: boolean;
};

export class ServiceDetailsStore {
	timePeriod: TimePeriod = {
		period: TimePeriodType.Last24Hours,
	};

	timePeriodZoomed: TimePeriod;

	service: Service;
	serviceModel: ServiceModelResponse;

	loading: boolean = false;
	loaded: boolean = false;

	selectedElement: MxCell;
	selectedQualifierId: number;
	documentsGridStore: GridStore<SummaryDocument>;

	constructor(public props: ServiceDetailsProps) {
		makeAutoObservable(this);
	}

	get readOnly() {
		return !ApplicationState.hasPermissions(Permission.ServiceUpdate);
	}

	get existsActiveDocuments() {
		return this.documentsGridStore.dataProvider.visibleRowsCount;
	}

	get serviceTime() {
		return getCurrentTimeStringInTimeZone(this.service.operatingSchedule.timeZone);
	}

	get timePeriodEffective() {
		return this.timePeriodZoomed ?? this.timePeriod;
	}

	get isTimezoneDifferent() {
		return isTimeZoneDifferentFromCurrent(this.service.operatingSchedule.timeZone);
	}

	updateTimePeriod = (timePeriod: TimePeriod) => {
		this.timePeriod = timePeriod;
		this.timePeriodZoomed = null;
	};

	async init() {
		this.loading = true;

		const canModifyMaintenance = ApplicationState.hasAnyPermission(
			Permission.ServiceMaintenanceCreate,
			Permission.ServiceMaintenanceUpdate,
			Permission.ServiceMaintenanceDelete,
		);

		const serviceResult = await apiFetch(
			getService({
				id: this.props.id,
				updateMode: this.props.updateMode && (canModifyMaintenance || !this.readOnly),
			}),
		);

		if (serviceResult.success) {
			this.service = serviceResult.data;

			if (this.service.modelId != null) {
				const modelResult = await apiFetch(
					getServiceModel({
						id: this.props.id,
					}),
				);

				if (modelResult.success) {
					this.serviceModel = modelResult.data;
				}
			}

			this.loaded = true;
		}

		this.documentsGridStore = new GridStore<SummaryDocument>({
			columns: [
				{
					field: 'type',
					title: i18n('Type'),
					renderer: (item) => (
						<div className={'file-type-icon'} dangerouslySetInnerHTML={{ __html: Renderer.fileIcon(item.type) }}></div>
					),
					width: 60,
				},
				{
					field: 'name',
					title: i18n('Name'),
					renderer: (item) => <Link onClick={() => this.onDocumentNameClick(item)}>{item.name}</Link>,
					width: 200,
				},
				{
					field: 'created',
					title: i18n('Created'),
					renderer: (item) =>
						moment(item.created).format(
							parseDateFormat(ApplicationState.dateTimeFormat).replace('.SSS', '').replace(':ss', ''),
						),
					minWidth: 100,
				},
			],
			dataProvider: new PlainResponseRemoteDataProvider({
				dataSource: summaryDocuments(this.props.id),
			}),
			selection: GridSelectionType.None,
			defaults: {
				sorting: [{ field: 'created', direction: 'asc' }],
			},
			customization: {
				height: 'by-content',
			},
		});

		this.loading = false;
	}

	refreshSummaryDocumentsGrid = () => {
		this.documentsGridStore.dataProvider.silentReload();
	};

	onDocumentNameClick(item: SummaryDocument) {
		const downloadUrl =
			item.type === 'application/x-url'
				? item.content
				: Settings.serverPath +
					'sessions/' +
					ApplicationState.sessionId +
					'/services/' +
					State.currentApp.id +
					'/documents/' +
					item.id +
					'/preview/';

		window.open(downloadUrl);
	}
}
